import { useMutation, useQuery, useSubscription } from '@apollo/client';
import { css } from '@emotion/core';
import {
    Button,
    CircularProgress,
    IconButton,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableRow,
    Theme,
    Tooltip,
    useTheme
} from '@material-ui/core';
import { Refresh as RefreshIcon } from '@material-ui/icons';
import { Skeleton } from '@material-ui/lab';
import { uniqBy } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import DocumentTitle from 'react-document-title';
import { Redirect } from 'react-router';

import { hasJobPermission } from 'shared/models/job';
import { BASELINE_SEARCH_SORT_RANK } from 'shared/models/search';

import { deleteSearch, fetchJobInfo, pauseJobSearches, updateSearch } from '../actions';
import { timeFrom } from '../common/timestamp';
import {
    JOB_SEARCH_PROGRESS_COUNTS,
    JOB_SEARCH_SOURCING_STATS_CACHE,
    JOB_SEARCH_USERS,
    JOB_SEARCHES,
    JOB_SEARCHES_SUB,
    JobSearch,
    JobSearchProgressCountsData,
    JobSearchSourcingStatsCacheData,
    REFRESH_JOB_SEARCH_SOURCING_STATS_CACHE,
    RefreshJobSearchSourcingStatsCacheData
} from '../graphql/queries/job-searches';
import { useAgent } from '../hooks/use-agent';
import { useLocalStorage } from '../hooks/use-local-storage';
import { useModal } from '../hooks/use-modal';
import { useReduxDispatch, useReduxState } from '../hooks/use-redux';
import { useSession } from '../hooks/use-session';
import { useUnsavedSearch } from '../search/use-unsaved-search';
import { JobSearchesState as JobSearchesStateType, Search, SearchStatus, State } from '../state';
import { JobOutreachButton } from './job-outreach-button';
import { JobPageHeader } from './job-page-header';
import { JobSearchesProgress } from './job-searches-progress';
import { JobSearchesStats } from './job-searches-stats';
import { SimilaritySearchButton } from './similarity-search-button';

const styles = css`
    &.job-searches-table.job-searches-section {
        margin-top: 0;
        margin-bottom: 0;
        flex: 0 1 auto;
        max-height: 100%;
        overflow: hidden;
        display: flex;
        flex-direction: column;
    }

    .MuiTableContainer-root {
        flex: 1 1 auto;
        overflow: auto;
        min-height: 0;

        &::-webkit-scrollbar:horizontal {
            border-top: thin solid #e0e0e0;
        }

        &::-webkit-scrollbar:vertical {
            border-left: thin solid #e0e0e0;
        }

        .MuiTableRow-hover {
            &.inactive {
                background: #fafafa;
                color: rgba(0, 0, 0, 0.6);
            }

            &:hover {
                background: rgba(0, 0, 0, 0.06);
            }
        }

        .MuiTableRow-root:last-child {
            td.MuiTableCell-root.MuiTableCell-body {
                border-bottom: none;
            }
        }

        .baseline {
            .MuiTableCell-root {
                font-weight: 500;
            }
        }
    }

    .job-searches-data-panel {
        .MuiTableCell-root.MuiTableCell-body {
            padding: 6px 16px;
        }

        .MuiTableCell-root.MuiTableCell-head {
            padding: 16px;
        }
    }
`;

const settingsStyles = css`
    .MuiInputBase-root {
        margin-right: 20px;

        &:before {
            border-bottom: none;
        }
        &:after {
            border-bottom: none;
        }
        &:hover:not(.Mui-disabled):before {
            border-bottom: none;
        }
    }
`;

const loadingTableStyle = (theme: Theme) => css`
    background: white;
    border: thin solid ${theme.palette.divider};
    border-radius: ${theme.shape.borderRadius}px;
`;

const ALL_USERS_OPTION = 'all';

export const JobSearches: React.FC<{ id: string }> = ({ id }) => {
    const theme = useTheme();
    const { user } = useSession();
    const { onOpenWithMessage } = useAgent();
    const [statusFilter, setStatusFilter] = useLocalStorage<'active' | 'paused' | 'activeOrPaused'>(
        'job-searches-status-filter-key',
        'active'
    );
    const [savedSearchOwner, setSavedSearchOwner] = useLocalStorage('job-searches-owner-filter-key', user.id);
    const [searchOwner, setSearchOwner] = useState(savedSearchOwner);
    const selectedUserId = searchOwner === ALL_USERS_OPTION ? null : searchOwner;
    const [unsavedSearch, setUnsavedSearch] = useUnsavedSearch(id);
    const [viewingSearchInfoId, setViewingSearchInfoId] = useState<string | null>(null);
    const [creatingSearch, setCreatingSearch] = useState(false);
    const [stats, setStats] = useState<'sourcing' | 'progress'>('sourcing');
    const [displayPercent, setDisplayPercent] = useState<'count' | 'percent' | 'both'>('count');
    const [displayDetail, setDisplayDetail] = useState(false);
    const dispatch = useReduxDispatch();
    const jobs = useReduxState((state: State) => state.jobs);
    const pendingRequests = useReduxState((state: State) => state.pendingRequests);
    const jobStages = useReduxState((state: State) => state.appConstants.constants.jobStages);
    const { getConfirmation } = useModal();
    const whereClause = selectedUserId
        ? { jobId: { _eq: id }, userId: { _eq: selectedUserId } }
        : { jobId: { _eq: id } };

    // Query to fetch full search data
    const { data: searchData, refetch: refetchSearches } = useQuery<
        {
            searches: Array<Search & { deleted?: boolean; agentSearch?: { id: string; title: string } }>;
        },
        { where: { jobId: { _eq: string }; userId?: { _eq: string } } }
    >(JOB_SEARCHES, {
        variables: { where: whereClause }
    });
    const { data: searchUsersData, refetch: refetchUsers } = useQuery<
        { searches: Array<{ status: SearchStatus; user: { id: string; name: string } }> },
        { jobId: string }
    >(JOB_SEARCH_USERS, { variables: { jobId: id } });

    // Fetch cached sourcing stats via GraphQL
    const {
        data: sourcingStatsData,
        loading: sourcingStatsLoading,
        refetch: refetchSourcingStats
    } = useQuery<JobSearchSourcingStatsCacheData, { where: { jobId: { _eq: string }; userId?: { _eq: string } } }>(
        JOB_SEARCH_SOURCING_STATS_CACHE,
        {
            variables: { where: whereClause }
        }
    );

    // Fetch progress counts via GraphQL (calls DB function directly)
    const {
        data: progressCountsData,
        loading: progressCountsLoading,
        refetch: refetchProgressCounts
    } = useQuery<JobSearchProgressCountsData, { jobId: string }>(JOB_SEARCH_PROGRESS_COUNTS, {
        variables: { jobId: id }
    });

    // Mutation to refresh sourcing stats cache
    const [refreshSourcingStatsCache, { loading: refreshingSourcingStats }] = useMutation<
        RefreshJobSearchSourcingStatsCacheData,
        { jobId: string; userId?: string }
    >(REFRESH_JOB_SEARCH_SOURCING_STATS_CACHE);

    // Track if subscription has initialized to avoid double fetch on mount
    const subscriptionInitialized = useRef(false);

    // Lightweight subscription to know when to refetch
    useSubscription<{ searches: JobSearch[] }, { where: { jobId: { _eq: string }; userId?: { _eq: string } } }>(
        JOB_SEARCHES_SUB,
        {
            onData: () => {
                // Skip initial callback to avoid double fetch - useQuery already fetched data
                if (subscriptionInitialized.current) {
                    refetchSearches();
                    refetchSourcingStats();
                } else {
                    subscriptionInitialized.current = true;
                }
            },
            variables: { where: whereClause }
        }
    );

    // Still need job info from Redux for now
    useEffect(() => {
        if (!jobs || !jobs.has(id)) {
            dispatch(fetchJobInfo(id));
        }
    }, [id, dispatch, jobs]);

    // Refetch stats when searches change
    useEffect(() => {
        if (searchData) {
            refetchSourcingStats();
            refetchProgressCounts();
            refetchUsers();
        }
    }, [searchData, refetchSourcingStats, refetchProgressCounts, refetchUsers]);

    const handleSetStatsView = (updatedStats: 'sourcing' | 'progress') => () => {
        let updatedDisplayPercent = displayPercent;
        let updatedDisplayDetail = displayDetail;
        if (updatedStats === 'progress' && updatedStats === 'progress') {
            updatedDisplayPercent =
                updatedDisplayPercent === 'count' ? 'both' : updatedDisplayPercent === 'both' ? 'percent' : 'count';
        } else if (updatedStats === 'sourcing' && updatedStats === 'sourcing') {
            updatedDisplayDetail = !updatedDisplayDetail;
        } else {
            updatedDisplayPercent = 'count';
            updatedDisplayDetail = false;
        }
        setStats(updatedStats);
        setDisplayPercent(updatedDisplayPercent);
        setDisplayDetail(updatedDisplayDetail);
    };

    const handleViewSearchInfo = (searchId: string) => {
        setViewingSearchInfoId(searchId);
    };

    const handleOpenNewSearchDialog = () => {
        setCreatingSearch(true);
    };

    const handleCloneSearch = (
        search: Search & {
            __typename: string;
            agentSearch?: { title: string; counts: { total: number }; conversationId: string };
        }
    ) => {
        // For agent searches, open agent with a message to re-run the search
        if (search.agentSearch) {
            const { title, counts, conversationId } = search.agentSearch;
            const message = `re-do the search query with title "${title}" that had ${counts.total} results, and re-share the preview with title "Copy of ${title}"`;
            onOpenWithMessage(message, conversationId);
            setViewingSearchInfoId(null);
            return;
        }

        // Regular search clone flow
        const { __typename, ...rest } = search;
        if (unsavedSearch?.status === SearchStatus.Initial) {
            dispatch(deleteSearch(unsavedSearch));
        }
        const searchToClone = Object.assign({}, rest, {
            createdAt: undefined,
            createdBy: user.id,
            id: undefined,
            jobId: id,
            maxProfileAgeInDays: -1,
            minSearchProfileScore: 0,
            modifiedAt: undefined,
            name: `Copy of ${search.name}`,
            searchProfilesScoreId: null,
            sortRank: undefined,
            status: SearchStatus.Initial,
            userId: user.id
        });
        setUnsavedSearch(searchToClone);
        setCreatingSearch(true);
        setViewingSearchInfoId(null);
    };

    const handleStatusFilterChange = (event: React.ChangeEvent<{ value: 'active' | 'paused' | 'activeOrPaused' }>) => {
        setStatusFilter(event.target.value);
    };

    const handleSearchOwnerChange = (event: React.ChangeEvent<{ value: string }>) => {
        setSearchOwner(event.target.value);
        if (event.target.value === ALL_USERS_OPTION || event.target.value === user.id) {
            setSavedSearchOwner(event.target.value);
        }
    };

    const filteredSearches = (jobSearch: JobSearchesStateType) => {
        const list = jobSearch?.searches ?? [];
        if (statusFilter === 'active') {
            return list.filter(
                (s) =>
                    (s.status !== SearchStatus.Paused && s.status !== SearchStatus.Archived) ||
                    s.sortRank === BASELINE_SEARCH_SORT_RANK
            );
        } else if (statusFilter === 'paused') {
            return list.filter((s) => s.status === SearchStatus.Paused || s.status === SearchStatus.Archived);
        } else {
            return list;
        }
    };

    const handlePauseAllSearches = () => {
        getConfirmation(
            () => {
                dispatch(pauseJobSearches(id));
            },
            'This will pause all of your searches in this job',
            'Confirm Pause Searches'
        );
    };

    const handleUpdateSearch = (searchId: string, updates: Partial<Search>, shouldRefetchSearches: boolean) => {
        dispatch(updateSearch(searchId, updates, shouldRefetchSearches));
    };

    const handleDeleteSearch = (search: Search) => {
        dispatch(deleteSearch(search));
    };

    const handleRefreshSourcingStats = async () => {
        if (!selectedUserId) {
            await refreshSourcingStatsCache({
                variables: { jobId: id }
            });
        } else {
            await refreshSourcingStatsCache({
                variables: { jobId: id, userId: selectedUserId }
            });
        }
        await refetchSourcingStats();
    };

    const job = jobs?.get?.(id);

    // GraphQL data is already in the correct shape thanks to field aliasing
    const searchStats = sourcingStatsData?.searchStats || [];
    const jobProgressInfo = progressCountsData?.progressCounts || [];

    // Create jobSearchData that combines query data with GraphQL stats
    const jobSearchData: JobSearchesStateType | undefined = searchData
        ? {
              progressCounts: jobProgressInfo,
              searchStats,
              searches: searchData.searches
          }
        : undefined;

    const leftJobSearchActions: JSX.Element[] = [];
    const rightJobSearchActions: JSX.Element[] = [];
    const pausingDisabled =
        !jobSearchData ||
        !jobSearchData.searches?.find((s) => s.status === SearchStatus.Active) ||
        pendingRequests.has(`job-${id}-pause-all-searches`);

    leftJobSearchActions.push(
        <Button onClick={handlePauseAllSearches} disabled={pausingDisabled} key="pause-all-searches">
            Pause All
        </Button>
    );

    const hasSearchCreatePermission = hasJobPermission(job?.recruiterPermissions.createSearch, user.id);

    const disabled = pendingRequests.has('search-create') || !hasSearchCreatePermission;

    const addNewSearchButton = (
        <Button
            onClick={handleOpenNewSearchDialog}
            disabled={disabled || !jobSearchData}
            style={disabled ? { pointerEvents: 'none' } : {}}
        >
            Add new search
        </Button>
    );

    const addNewSearchButtonWithTooltip = (
        <Tooltip title={'Search create disallowed - contact job AM'}>
            <span>{addNewSearchButton}</span>
        </Tooltip>
    );

    rightJobSearchActions.push(
        <div key="add-search">{!hasSearchCreatePermission ? addNewSearchButtonWithTooltip : addNewSearchButton}</div>
    );

    const jobSearches = filteredSearches(jobSearchData);

    if (creatingSearch) {
        const creatingSearchUrl = `/job/${id}/search/`;
        return <Redirect to={creatingSearchUrl} push={true} />;
    } else if (viewingSearchInfoId) {
        const viewingSearchUrl = `/job/${id}/search/${viewingSearchInfoId}/total`;
        return <Redirect to={viewingSearchUrl} push={true} />;
    }

    const isLoadingData = sourcingStatsLoading || progressCountsLoading;
    const loadingRowsCount = 5;
    const loadingRows =
        !jobSearchData || isLoadingData
            ? Array.from({ length: loadingRowsCount }).map((_, i) => (
                  <TableRow key={i}>
                      <TableCell>
                          <Skeleton className="interview skeleton" variant="rect" key={i} />
                      </TableCell>
                  </TableRow>
              ))
            : null;

    const jobSearchesFiltered =
        searchOwner === ALL_USERS_OPTION
            ? jobSearches
            : jobSearches.filter((s) => s.userId === searchOwner || s.sortRank === BASELINE_SEARCH_SORT_RANK);
    const searchesList = jobSearchesFiltered;
    const statsTable =
        !jobSearchData || isLoadingData ? (
            <Table css={loadingTableStyle(theme)}>
                <TableBody>{loadingRows}</TableBody>
            </Table>
        ) : stats === 'sourcing' ? (
            <JobSearchesStats
                displayDetail={displayDetail}
                job={job}
                searches={searchesList}
                searchStats={searchStats}
                onDisplaySearchInfo={handleViewSearchInfo}
                onCloneSearch={handleCloneSearch}
                onUpdateSearch={handleUpdateSearch}
                onDeleteSearch={handleDeleteSearch}
            />
        ) : (
            <JobSearchesProgress
                job={job}
                progressCounts={jobProgressInfo}
                searches={searchesList}
                onDisplaySearchInfo={handleViewSearchInfo}
                displayPercent={displayPercent}
                onCloneSearch={handleCloneSearch}
                jobStages={jobStages}
                onUpdateSearch={handleUpdateSearch}
                onDeleteSearch={handleDeleteSearch}
            />
        );
    const loadingSize = 20;
    const loadingThickness = 2;

    // Calculate earliest cache time for tooltip
    const earliestCacheTime = searchStats.length ? Math.min(...searchStats.map((s) => s.refreshedAt)) : 0;
    const cacheAgeDisplay = timeFrom(earliestCacheTime);
    const refreshTooltip = `Cached ${cacheAgeDisplay}. Click to refresh.`;

    const refreshButton = (
        <span className="refresh-button tab">
            <Tooltip title={refreshTooltip}>
                <IconButton
                    size="small"
                    onClick={handleRefreshSourcingStats}
                    disabled={refreshingSourcingStats || isLoadingData}
                >
                    <RefreshIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        </span>
    );

    const statsSelector = pendingRequests.has(`job-searches-stats-${id}`) ? (
        <div className="job-searches-table-stats-selector loading">
            Loading Stats
            <CircularProgress size={loadingSize} thickness={loadingThickness} style={{ marginLeft: 15, bottom: 1 }} />
        </div>
    ) : (
        <div className="job-searches-table-stats-selector">
            <span className={stats === 'sourcing' ? 'active tab' : 'tab'} onClick={handleSetStatsView('sourcing')}>
                Sourcing
                {displayDetail ? ' (detailed stats)' : ''}
            </span>
            <span className={stats === 'progress' ? 'active tab' : 'tab'} onClick={handleSetStatsView('progress')}>
                Candidate Progress
                {displayPercent === 'count' ? '' : displayPercent === 'both' ? ' (%)' : '%'}
            </span>
            {refreshButton}
        </div>
    );

    const filterLabels: { [status: string]: string } = {
        active: 'Active',
        activeOrPaused: 'Active / Paused',
        paused: 'Paused'
    };

    const searchStatusFilterOptions = ['active', 'paused', 'activeOrPaused'].map((s) => (
        <MenuItem key={s} value={s}>
            {filterLabels[s]}
        </MenuItem>
    ));
    const searchStatusFilterSelect = (
        <Select value={statusFilter} onChange={handleStatusFilterChange}>
            {searchStatusFilterOptions}
        </Select>
    );

    const searchOwnerUsers = searchUsersData?.searches?.filter(
        (s) =>
            s.user.id !== user.id &&
            (statusFilter === 'activeOrPaused' ||
                (statusFilter === 'active' && s.status !== SearchStatus.Paused && s.status !== SearchStatus.Archived) ||
                (statusFilter === 'paused' && (s.status === SearchStatus.Paused || s.status === SearchStatus.Archived)))
    );
    const userIdOpts = uniqBy(searchOwnerUsers, (s) => s.user.id)
        .sort((s1, s2) => s1.user.name.localeCompare(s2.user.name))
        .map((s) => (
            <MenuItem key={s.user.id} value={s.user.id}>
                {s.user.name}
            </MenuItem>
        ));

    const searchOwnerSelect = (
        <Select value={searchOwner} onChange={handleSearchOwnerChange}>
            <MenuItem value={ALL_USERS_OPTION}>All</MenuItem>
            <MenuItem value={user.id}>{user.name.full}</MenuItem>
            {userIdOpts}
        </Select>
    );

    const renderSimilaritySearchButton = (onClick: () => void) => (
        <IconButton onClick={onClick} className="similarity-button">
            <i className="fas fa-magic" />
        </IconButton>
    );

    return (
        <DocumentTitle title="Job Searches">
            <div id="container">
                <JobPageHeader
                    jobId={id}
                    actions={[<JobOutreachButton jobId={id} iconColor="white" iconSize="default" key="outreach" />]}
                    activeTab="Searches"
                />
                <div id="content" className="flex-fill">
                    <div className="job-searches-container">
                        <div className="job-searches-section">
                            <div className="job-searches-header">
                                <div className="job-searches-job-settings" css={settingsStyles}>
                                    <div className="job-searches-filter-select with-border">
                                        {searchStatusFilterSelect}
                                    </div>
                                    <div className="job-searches-filter-select">{searchOwnerSelect}</div>
                                </div>
                                {statsSelector}
                            </div>
                        </div>
                        <div className="job-searches-section job-searches-table" css={styles}>
                            {statsTable}
                        </div>
                        <div className="job-searches-section job-searches-actions">
                            <div>{leftJobSearchActions}</div>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                {rightJobSearchActions}
                                <SimilaritySearchButton renderElement={renderSimilaritySearchButton} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </DocumentTitle>
    );
};
